import React from "react";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Layers, Plus, Scissors, Trash2 } from "lucide-react";
import { ActionMenu, ActionMenuDivider, ActionMenuItem, AppButton, Checkbox, FieldGroup, FormField, IconButton, Input } from "../ui";
import SubtopicsEditor from "./SubtopicsEditor";

/**
 * One topic of a module: its title and minutes on the row, and — opened — its
 * subtopics and the admin's say in where classes break.
 *
 * Enter in the title adds the next topic and moves there, so a module can be
 * typed out without reaching for the mouse.
 *
 * Props:
 *   topic, index, count, open, onToggle
 *   error           — message for this topic (from validation)
 *   autoFocus       — focus the title on mount (a topic just added)
 *   onChange(patch), onAddAfter(), onMove(delta), onRemove()
 *   resources       — { count, onOpen, disabledReason } | null — the topic's resources
 */
const TopicRow = ({
  topic, index, count, open, onToggle, error, autoFocus,
  onChange, onAddAfter, onMove, onRemove, resources,
}) => {
  const name = topic.title.trim() || `Topic ${index + 1}`;
  const subCount = topic.subtopics.length;

  return (
    <li className="tw-flex tw-flex-col tw-gap-2 tw-rounded-md tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-px-2.5 tw-py-2">
      <div className="tw-flex tw-items-start tw-gap-1.5">
        <IconButton
          size="sm"
          icon={open ? ChevronDown : ChevronRight}
          label={open ? `Hide details of ${name}` : `Show details of ${name}`}
          aria-expanded={open}
          onClick={onToggle}
          className="tw-mt-1"
        />
        <div className="tw-grid tw-min-w-0 tw-flex-1 tw-gap-2 min-[576px]:tw-grid-cols-[minmax(0,1fr)_7.5rem]">
          <Input
            value={topic.title}
            invalid={!!error}
            onChange={(e) => onChange({ title: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onAddAfter();
              }
            }}
            placeholder={`Topic ${index + 1} — e.g. Loops`}
            aria-label={`Topic ${index + 1} title`}
            maxLength={200}
            autoFocus={autoFocus}
          />
          <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-content-muted">
            <Input
              type="number"
              min={5}
              max={600}
              step={5}
              className="tw-min-w-0 tw-flex-1 tw-tabular-nums"
              value={topic.minutes}
              onChange={(e) => onChange({ minutes: e.target.value })}
              aria-label={`Minutes for ${name}`}
            />
            min
          </span>
        </div>
        <ActionMenu label={`Actions for ${name}`}>
          <ActionMenuItem onClick={onAddAfter}><Plus size={14} aria-hidden="true" /> Add a topic below</ActionMenuItem>
          <ActionMenuItem onClick={() => onMove(-1)} disabled={index === 0}><ArrowUp size={14} aria-hidden="true" /> Move up</ActionMenuItem>
          <ActionMenuItem onClick={() => onMove(1)} disabled={index === count - 1}><ArrowDown size={14} aria-hidden="true" /> Move down</ActionMenuItem>
          <ActionMenuDivider />
          <ActionMenuItem danger onClick={onRemove} disabled={count <= 1}><Trash2 size={14} aria-hidden="true" /> Delete topic</ActionMenuItem>
        </ActionMenu>
      </div>

      {error && <p role="alert" className="tw-m-0 tw-ps-9 tw-text-xs tw-text-danger">{error}</p>}

      {!open && (subCount > 0 || topic.startsNewClass || resources) && (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-ps-9 tw-text-xs tw-text-content-muted">
          {subCount > 0 && <span>{subCount} subtopic{subCount === 1 ? "" : "s"}</span>}
          {topic.startsNewClass && (
            <span className="tw-inline-flex tw-items-center tw-gap-1"><Scissors size={11} aria-hidden="true" /> New class starts here</span>
          )}
          {resources && <ResourcesButton resources={resources} />}
        </div>
      )}

      {open && (
        <div className="tw-flex tw-flex-col tw-gap-3 tw-border-0 tw-border-t tw-border-dashed tw-border-line tw-pt-3 tw-ps-9">
          <FieldGroup legend="Subtopics">
            <SubtopicsEditor subtopics={topic.subtopics} topicTitle={name} onChange={(subtopics) => onChange({ subtopics })} />
          </FieldGroup>
          <div className="tw-flex tw-flex-col tw-gap-2">
            <Checkbox
              checked={topic.startsNewClass}
              onChange={(e) => onChange({ startsNewClass: e.target.checked, ...(e.target.checked ? {} : { classTitle: "" }) })}
              label="Start a new class with this topic"
              description="Otherwise topics share a class while they fit its length."
            />
            {topic.startsNewClass && (
              <FormField label="Name that class (optional)" hint="Left empty, it is named after its topics.">
                <Input size="sm" value={topic.classTitle} onChange={(e) => onChange({ classTitle: e.target.value })} maxLength={200} placeholder="e.g. Lab: build a calculator" />
              </FormField>
            )}
          </div>
          {resources && <div><ResourcesButton resources={resources} /></div>}
        </div>
      )}
    </li>
  );
};

/** "Resources · 2" — opens the topic's resources, or says why it cannot yet. */
function ResourcesButton({ resources }) {
  if (resources.disabledReason) {
    return <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-content-subtle"><Layers size={11} aria-hidden="true" /> {resources.disabledReason}</span>;
  }
  return (
    <AppButton size="xs" variant="ghost" onClick={resources.onOpen}>
      <Layers size={12} aria-hidden="true" /> Resources{resources.count ? ` · ${resources.count}` : ""}
    </AppButton>
  );
}

export default TopicRow;
